import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, XCircle, Loader2, ArrowRight, Home, Sparkles } from "lucide-react";

const PaymentSuccess = ({ user, API }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const isOnboarding = searchParams.get("onboarding") === "true";
  
  const [status, setStatus] = useState("checking"); // checking, success, failed, expired
  const [planInfo, setPlanInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error("Failed to check payment status");
        }

        const data = await response.json();

        if (data.payment_status === "paid") {
          setStatus("success");
          setPlanInfo({
            planId: data.plan_id,
            planName: data.plan_name
          });
          
          // ALWAYS try to activate subscription after successful payment
          try {
            const activateResponse = await fetch(`${API}/subscriptions/activate?session_id=${sessionId}`, {
              method: "POST",
              credentials: "include"
            });
            
            if (activateResponse.ok) {
              const activateData = await activateResponse.json();
              console.log("Subscription activated:", activateData);
              // Update plan info from activation response
              if (activateData.plan_id) {
                setPlanInfo({
                  planId: activateData.plan_id,
                  planName: activateData.plan_name || activateData.plan_id
                });
              }
            } else {
              console.error("Failed to activate subscription:", await activateResponse.text());
            }
          } catch (e) {
            console.error("Failed to activate subscription:", e);
          }
          
          return true; // Stop polling
        } else if (data.status === "expired") {
          setStatus("expired");
          return true; // Stop polling
        }

        // Continue polling if still pending
        return false;
      } catch (error) {
        console.error("Error checking payment:", error);
        if (attempts >= 4) {
          setStatus("failed");
          return true;
        }
        return false;
      }
    };

    // Initial check
    checkPaymentStatus().then((shouldStop) => {
      if (!shouldStop) {
        // Poll every 2 seconds, max 5 attempts
        const pollInterval = setInterval(async () => {
          setAttempts((prev) => prev + 1);
          const shouldStop = await checkPaymentStatus();
          if (shouldStop || attempts >= 4) {
            clearInterval(pollInterval);
          }
        }, 2000);

        return () => clearInterval(pollInterval);
      }
    });
  }, [sessionId, API, attempts, isOnboarding]);

  const renderContent = () => {
    switch (status) {
      case "checking":
        return (
          <div className="text-center py-12" data-testid="payment-checking">
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2">Verifying Payment</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </div>
        );

      case "success":
        return (
          <div className="text-center py-12" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold mb-2">
              {isOnboarding ? "Welcome to Trade Office!" : "Payment Successful!"}
            </h2>
            <p className="text-xl text-muted-foreground mb-4">
              {planInfo?.planName || "Your subscription"} is now active
            </p>
            
            {isOnboarding && (
              <div className="bg-primary/10 rounded-lg p-4 mb-6 flex items-center justify-center gap-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="text-sm">Your account is ready to create professional quotes!</span>
              </div>
            )}
            
            <p className="text-muted-foreground mb-8">
              Your quote credits have been added to your account. 
              {isOnboarding ? " Let's create your first quote!" : " You're all set!"}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate("/quote/new")}
                data-testid="create-quote-btn"
              >
                {isOnboarding ? "Create Your First Quote" : "Create a Quote"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate("/dashboard")}
                data-testid="go-dashboard-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </div>
        );

      case "expired":
        return (
          <div className="text-center py-12" data-testid="payment-expired">
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Session Expired</h2>
            <p className="text-muted-foreground mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate("/pricing")}
              data-testid="retry-payment-btn"
            >
              Back to Pricing
            </Button>
          </div>
        );

      case "failed":
      default:
        return (
          <div className="text-center py-12" data-testid="payment-failed">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Payment Issue</h2>
            <p className="text-muted-foreground mb-8">
              We couldn't verify your payment. If you were charged, 
              please contact support with your session ID.
            </p>
            {sessionId && (
              <p className="text-xs text-muted-foreground mb-4 font-mono">
                Session: {sessionId}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate("/pricing")}
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate("/dashboard")}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full bg-card border-white/10">
        <CardHeader className="text-center pb-0">
          <CardTitle className="text-xl text-muted-foreground">
            Trade Office
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
